import { onBeforeUnmount, type Ref, watch } from 'vue'

// Module-level stack of currently-open dismissible overlays (modals,
// dropdowns, popovers), in the order they opened. An Escape keypress
// should dismiss exactly one layer — the most-recently-opened one — not
// every open overlay that happens to run its own document-level
// listener.
//
// Root cause this fixes (#2330 / #2331): StemneModal registered an
// unconditional `document.addEventListener('keydown', ...)` guarded only
// by its own `props.show`. Two StemneModal instances open at once (a
// parent edit modal + a sibling StemneConfirmModal delete-confirm, or a
// StemneSelect dropdown open inside a StemneModal) each had their own
// listener fire on the SAME Escape keypress — closing the confirm dialog
// AND the parent edit modal, or closing a dropdown AND the host modal.
// stopPropagation doesn't fix the modal+modal case (both listeners sit on
// the same `document` target; only stopImmediatePropagation stops
// sibling listeners on the same target, and it doesn't tell you which one
// should win). A shared ordering stack does: only the overlay that opened
// most recently reacts.
let stack: symbol[] = []

function isTopmost(id: symbol): boolean {
  return stack.length > 0 && stack[stack.length - 1] === id
}

/**
 * Registers a dismissible overlay (modal, dropdown, popover) on the
 * shared escape stack while `isOpen` is true, and returns a keydown
 * handler that calls `onEscape` only when this overlay is the topmost
 * (most-recently-opened) one currently on the stack. Wire the returned
 * handler to whatever `keydown` source the overlay already listens on
 * (a document listener for a modal, an element `@keydown` for a
 * dropdown trigger) — the caller keeps its own preventDefault/close
 * logic, this just gates whether it should run.
 */
export function useEscapeStack(isOpen: Ref<boolean>, onEscape: (ev: KeyboardEvent) => void) {
  const id = Symbol('overlay')

  function push() {
    if (!stack.includes(id)) stack.push(id)
  }
  function pop() {
    stack = stack.filter((entry) => entry !== id)
  }

  // `flush: 'sync'` — an overlay closing and the very next Escape keydown
  // (e.g. this handler closing itself, then a fast second press) must see
  // an up-to-date stack; Vue's default pre-flush timing would otherwise
  // defer the pop to a microtask past a same-tick keydown.
  watch(isOpen, (open) => (open ? push() : pop()), { immediate: true, flush: 'sync' })
  onBeforeUnmount(pop)

  return function handleEscapeKeydown(ev: KeyboardEvent) {
    if (ev.key !== 'Escape') return
    if (!isOpen.value) return
    if (!isTopmost(id)) return
    onEscape(ev)
  }
}
